import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { JsonRpcProvider } from 'ethers';

// Simple in-memory cache to prevent duplicate network calls
const avatarCache = new Map();
const ensCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const useENSResolver = (address, ensName = null) => {
  const [ens, setEns] = useState(ensName || null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const isMounted = useRef(true);

  // Shared provider with fallback RPCs (same as Zhat backend)
  const provider = useMemo(() => {
    const rpcUrls = [
    'https://rpc.ankr.com/eth/dec8e5317af4d9c8cb41cc5bfaef26700319860751215bc62794cd3cafdd691d', // ✅ Your key
    'https://ethereum.publicnode.com',  // ✅ Backup 1
    'https://cloudflare-eth.com'        // ✅ Backup 2
    ];
    
    return new JsonRpcProvider(rpcUrls[0]); // Use first RPC, others are fallbacks in resolve logic
  }, []);

  // Process avatar URL
  const processAvatarUrl = useCallback((avatarRecord, domain) => {
    if (!avatarRecord || avatarRecord.trim() === '') return '';
    
    // Handle IPFS URLs
    if (avatarRecord.startsWith('ipfs://')) {
      const ipfsHash = avatarRecord.replace('ipfs://', '');
      return `https://cloudflare-ipfs.com/ipfs/${ipfsHash}`;
    }
    
    // Handle HTTP/HTTPS URLs directly
    if (avatarRecord.startsWith('http://') || avatarRecord.startsWith('https://')) {
      return avatarRecord;
    }
    
    // Handle NFT avatars via ENS metadata service
    if (avatarRecord.includes('eip155:')) {
      return `https://metadata.ens.domains/mainnet/avatar/${domain}`;
    }
    
    return '';
  }, []);

  // Check cache first
  const checkCache = useCallback((addr, targetENS) => {
    const now = Date.now();
    
    // Check ENS cache
    if (!targetENS) {
      const cachedENS = ensCache.get(addr);
      if (cachedENS && (now - cachedENS.timestamp < CACHE_DURATION)) {
        targetENS = cachedENS.data;
      }
    }
    
    // Check avatar cache
    const cacheKey = targetENS || addr;
    const cachedAvatar = avatarCache.get(cacheKey);
    if (cachedAvatar && (now - cachedAvatar.timestamp < CACHE_DURATION)) {
      return { ens: targetENS, avatar: cachedAvatar.data };
    }
    
    return null;
  }, []);

  // Update cache
  const updateCache = useCallback((addr, ensName, avatarUrl) => {
    const now = Date.now();
    
    if (ensName && !ensCache.has(addr)) {
      ensCache.set(addr, { data: ensName, timestamp: now });
    }
    
    const cacheKey = ensName || addr;
    if (avatarUrl && !avatarCache.has(cacheKey)) {
      avatarCache.set(cacheKey, { data: avatarUrl, timestamp: now });
    }
  }, []);

  // Main resolution function
  const resolveAddress = useCallback(async (addr, providedENS = null) => {
    if (!addr) return;
    
    // Check cache first
    const cached = checkCache(addr, providedENS);
    if (cached) {
      if (isMounted.current) {
        setEns(cached.ens);
        setAvatarUrl(cached.avatar);
      }
      return cached;
    }

    if (isMounted.current) {
      setIsLoading(true);
      setError(null);
      setEns(providedENS || null);
      setAvatarUrl(null);
    }

    try {
      let targetENS = providedENS;
      
      // 1. REVERSE LOOKUP if no ENS provided
      if (!targetENS) {
        try {
          targetENS = await provider.lookupAddress(addr);
          if (targetENS && isMounted.current) {
            setEns(targetENS);
            updateCache(addr, targetENS, null);
          }
        } catch (lookupError) {
          console.warn('Reverse lookup failed:', lookupError.message);
        }
      }

      // 2. GET AVATAR if we have ENS
      if (targetENS) {
        try {
          const resolver = await provider.getResolver(targetENS);
          if (resolver) {
            const avatarRecord = await resolver.getText('avatar');
            if (avatarRecord) {
              const processedUrl = processAvatarUrl(avatarRecord, targetENS);
              if (processedUrl && isMounted.current) {
                setAvatarUrl(processedUrl);
                updateCache(addr, targetENS, processedUrl);
              }
            }
          }
        } catch (resolverError) {
          console.warn('Resolver lookup failed:', resolverError.message);
        }
      }

    } catch (err) {
      console.error('ENS resolution error:', err);
      if (isMounted.current) {
        setError(err.message || 'Failed to resolve ENS');
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [provider, processAvatarUrl, checkCache, updateCache]);

  // Auto-resolve when address or ensName changes
  useEffect(() => {
    isMounted.current = true;
    
    if (address || ensName) {
      resolveAddress(address, ensName);
    }

    return () => {
      isMounted.current = false;
    };
  }, [address, ensName, resolveAddress]);

  // Manual retry function
  const retry = useCallback(() => {
    resolveAddress(address, ensName);
  }, [address, ensName, resolveAddress]);

  return { 
    ensName: ens, 
    avatarUrl, 
    isLoading, 
    error, 
    retry 
  };
};

// Clear cache utility (can be called from anywhere)
export const clearAvatarCache = () => {
  avatarCache.clear();
  ensCache.clear();
  console.log('Avatar cache cleared');
};

export default useENSResolver;
